import React, { useState, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Search, Users, Lock, Layers, User as UserIcon, Trash2, Check, Eye, Clock, GripVertical,
} from 'lucide-react';
import { useUserData } from '../contexts/UserDataContext';
import './TemplateLibrary.css';

const Badge = ({ children, variant = 'default' }) => {
  const cls = {
    builtin: 'bg-gray-700/60 text-gray-300 border-gray-600',
    team: 'bg-teal-900/40 text-teal-300 border-teal-700/60',
    mine: 'bg-blue-900/40 text-blue-200 border-blue-800/60',
  }[variant] || 'bg-gray-700/40 text-gray-300 border-gray-600';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${cls}`}>
      {children}
    </span>
  );
};

const TemplateCard = ({
  tpl, isActive, onUse, onDelete,
  draggable, isDragging, isDropTarget,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
}) => {
  const ownedByMe = tpl.is_mine === true;
  const previewBody = (tpl.paragraph || '').split('\n').slice(0, 3).join('\n');

  return (
    <div
      className={`tpl-card ${isActive ? 'tpl-card-active' : ''} ${isDragging ? 'tpl-card-dragging' : ''} ${isDropTarget ? 'tpl-card-drop-target' : ''}`}
      data-testid={`library-card-${tpl.id}`}
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart?.(e, tpl) : undefined}
      onDragOver={draggable ? (e) => onDragOver?.(e, tpl) : undefined}
      onDragLeave={draggable ? (e) => onDragLeave?.(e, tpl) : undefined}
      onDrop={draggable ? (e) => onDrop?.(e, tpl) : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
    >
      {draggable && (
        <div
          className="tpl-drag-handle"
          title="Drag to reorder"
          data-testid={`library-drag-handle-${tpl.id}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </div>
      )}
      {/* Mini preview */}
      <div className="tpl-preview">
        <div className="tpl-preview-bg" />
        <div className="tpl-preview-content">
          <div className="tpl-preview-title">{tpl.title || 'Untitled'}</div>
          {previewBody && (
            <div className="tpl-preview-paragraph">{previewBody}</div>
          )}
          <div className="tpl-preview-lower">
            {(tpl.lower_text || tpl.lowerText || '').toUpperCase()}
          </div>
        </div>
      </div>

      {/* Meta */}
      <div className="tpl-meta">
        <div className="tpl-name" title={tpl.name}>{tpl.name}</div>
        <div className="tpl-badges">
          {tpl.builtin && <Badge variant="builtin"><Layers className="w-3 h-3" /> Built-in</Badge>}
          {!tpl.builtin && ownedByMe && tpl.visibility !== 'team' && (
            <Badge variant="mine"><Lock className="w-3 h-3" /> Private</Badge>
          )}
          {!tpl.builtin && tpl.visibility === 'team' && (
            <Badge variant="team"><Users className="w-3 h-3" /> Team</Badge>
          )}
        </div>
        {!tpl.builtin && (
          <div className="tpl-owner">
            <UserIcon className="w-3 h-3" />
            {ownedByMe ? 'You' : (tpl.owner_name || 'Teammate')}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="tpl-actions">
        <Button
          size="sm"
          onClick={() => onUse(tpl)}
          className={`flex-1 h-8 text-xs ${isActive
            ? 'bg-teal-600/30 text-teal-200 hover:bg-teal-600/40'
            : 'bg-teal-500 hover:bg-teal-400 text-gray-900'}`}
          data-testid={`library-use-${tpl.id}`}
        >
          {isActive ? (
            <><Check className="w-3.5 h-3.5 mr-1" /> Active</>
          ) : (
            <><Eye className="w-3.5 h-3.5 mr-1" /> Use</>
          )}
        </Button>
        {!tpl.builtin && ownedByMe && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(tpl)}
            className="border-red-900/60 bg-red-950/30 text-red-300 hover:bg-red-900/40 hover:text-red-100 h-8 px-2"
            title="Delete"
            data-testid={`library-delete-${tpl.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const TemplateLibrary = ({ open, onOpenChange, currentTemplate, onPick }) => {
  const { allTemplates, customTemplates, deleteTemplate, reorderTemplates, recentlyUsedTemplateIds } = useUserData();
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');

  // Drag-and-drop reorder state.
  // We only allow reordering of custom templates (built-ins stay pinned at top
  // in their fixed declaration order). The persisted order is global per
  // template — private templates only affect their owner; team templates'
  // order is shared with the whole team.
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const dragSourceRef = useRef(null);

  const canReorder = (tpl) => {
    if (!tpl || tpl.builtin) return false;
    // Owner can reorder their private templates; anyone in the team can
    // reorder team-shared templates (since order_index lives on the doc).
    return tpl.is_mine === true || tpl.visibility === 'team';
  };

  const handleDragStart = (e, tpl) => {
    if (!canReorder(tpl)) {
      e.preventDefault();
      return;
    }
    dragSourceRef.current = tpl.id;
    setDraggingId(tpl.id);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', tpl.id);
    } catch (_) { /* some browsers throw on synthetic events */ }
  };

  const handleDragOver = (e, tpl) => {
    if (!dragSourceRef.current || !canReorder(tpl)) return;
    if (dragSourceRef.current === tpl.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(tpl.id);
  };

  const handleDragLeave = (e, tpl) => {
    if (dropTargetId === tpl.id) setDropTargetId(null);
  };

  const handleDrop = async (e, targetTpl) => {
    e.preventDefault();
    const sourceId = dragSourceRef.current;
    dragSourceRef.current = null;
    setDraggingId(null);
    setDropTargetId(null);
    if (!sourceId || !targetTpl || sourceId === targetTpl.id) return;
    if (!canReorder(targetTpl)) return;

    // Build new order from the current customTemplates list (the canonical
    // order). We move sourceId to the position of targetTpl.
    const ids = customTemplates.map((t) => t.id);
    const fromIdx = ids.indexOf(sourceId);
    const toIdx = ids.indexOf(targetTpl.id);
    if (fromIdx === -1 || toIdx === -1) return;
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, sourceId);
    await reorderTemplates(ids);
  };

  const handleDragEnd = () => {
    dragSourceRef.current = null;
    setDraggingId(null);
    setDropTargetId(null);
  };

  // Build "Recently Used" list — preserve usage order, dedupe, ignore templates no longer in the catalog
  const recentlyUsed = useMemo(() => {
    if (query.trim() || tab !== 'all') return [];
    const seen = new Set();
    const out = [];
    for (const id of recentlyUsedTemplateIds) {
      if (seen.has(id)) continue;
      const tpl = allTemplates.find((t) => t.id === id);
      if (tpl) {
        out.push(tpl);
        seen.add(id);
      }
      if (out.length >= 5) break;
    }
    return out;
  }, [recentlyUsedTemplateIds, allTemplates, query, tab]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTemplates.filter((t) => {
      if (tab === 'mine' && (t.builtin || t.is_mine === false)) return false;
      if (tab === 'team' && t.visibility !== 'team') return false;
      if (tab === 'builtin' && !t.builtin) return false;
      if (!q) return true;
      return (
        t.name?.toLowerCase().includes(q) ||
        t.title?.toLowerCase().includes(q) ||
        (t.paragraph || '').toLowerCase().includes(q) ||
        (t.owner_name || '').toLowerCase().includes(q)
      );
    });
  }, [allTemplates, query, tab]);

  const counts = useMemo(() => ({
    all: allTemplates.length,
    mine: allTemplates.filter((t) => !t.builtin && t.is_mine !== false).length,
    team: allTemplates.filter((t) => t.visibility === 'team').length,
    builtin: allTemplates.filter((t) => t.builtin).length,
  }), [allTemplates]);

  const handleUse = (tpl) => {
    onPick(tpl.id);
    onOpenChange(false);
  };

  const handleDelete = async (tpl) => {
    const ok = window.confirm(`Delete template "${tpl.name}"?`);
    if (!ok) return;
    try {
      await deleteTemplate(tpl.id);
    } catch (e) {
      // surfaced in console
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="library-dialog max-w-4xl bg-gray-950 border-gray-800 text-white p-0 overflow-hidden"
        data-testid="template-library"
      >
        <DialogHeader className="library-header">
          <DialogTitle className="text-xl font-light text-white tracking-tight">
            Template Library
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Browse and apply built-in presets, your private templates, and templates shared by your team. Drag the <span className="inline-flex items-center gap-0.5 text-teal-300"><GripVertical className="w-3 h-3" /> handle</span> on custom or team cards to reorder.
          </DialogDescription>
        </DialogHeader>

        <div className="library-toolbar">
          <div className="library-search">
            <Search className="w-4 h-4 text-gray-500" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, title, paragraph, or owner…"
              className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 h-9"
              data-testid="library-search"
            />
          </div>

          <Tabs value={tab} onValueChange={setTab} className="library-tabs">
            <TabsList className="bg-gray-900 border border-gray-800">
              <TabsTrigger value="all" data-testid="library-tab-all">
                All <span className="ml-1.5 text-[10px] text-gray-500">{counts.all}</span>
              </TabsTrigger>
              <TabsTrigger value="mine" data-testid="library-tab-mine">
                Mine <span className="ml-1.5 text-[10px] text-gray-500">{counts.mine}</span>
              </TabsTrigger>
              <TabsTrigger value="team" data-testid="library-tab-team">
                Team <span className="ml-1.5 text-[10px] text-gray-500">{counts.team}</span>
              </TabsTrigger>
              <TabsTrigger value="builtin" data-testid="library-tab-builtin">
                Built-in <span className="ml-1.5 text-[10px] text-gray-500">{counts.builtin}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="library-grid-wrapper">
          {recentlyUsed.length > 0 && (
            <div className="library-section" data-testid="library-recently-used">
              <div className="library-section-header">
                <Clock className="w-3.5 h-3.5 text-teal-400" />
                <span>Recently Used</span>
                <span className="library-section-count">{recentlyUsed.length}</span>
              </div>
              <div className="library-grid library-grid-inline">
                {recentlyUsed.map((tpl) => (
                  <TemplateCard
                    key={`recent-${tpl.id}`}
                    tpl={tpl}
                    isActive={tpl.id === currentTemplate}
                    onUse={handleUse}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
              <div className="library-section-divider" />
              <div className="library-section-header">
                <Layers className="w-3.5 h-3.5 text-gray-400" />
                <span>All Templates</span>
                <span className="library-section-count">{filtered.length}</span>
              </div>
            </div>
          )}

          <div className="library-grid">
            {filtered.length === 0 ? (
              <div className="library-empty" data-testid="library-empty">
                <Layers className="w-10 h-10 text-gray-700 mb-3" />
                <div className="text-gray-300 font-medium mb-1">No templates match</div>
                <div className="text-gray-500 text-sm">
                  {query ? 'Try a different search term' : 'Create a new template from the control panel'}
                </div>
              </div>
            ) : (
              filtered.map((tpl) => {
                const reorderable = canReorder(tpl);
                return (
                  <TemplateCard
                    key={tpl.id}
                    tpl={tpl}
                    isActive={tpl.id === currentTemplate}
                    onUse={handleUse}
                    onDelete={handleDelete}
                    draggable={reorderable}
                    isDragging={draggingId === tpl.id}
                    isDropTarget={dropTargetId === tpl.id}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                  />
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateLibrary;
